/**
 * videoSourceUtils.js
 * 
 * Platform detection + embed URL extraction for the Tvadi Playlist Maker.
 * Ported from the main player's platformUtils.ts (React Native) to plain JS for web.
 * 
 * Drop into: /var/www/react/frontend/src/new_creatorPlaylist/videoSourceUtils.js
 */

// ══════════════════════════════════════════════════
// PLATFORM DETECTION
// ══════════════════════════════════════════════════

export function getVideoPlatform(url) {
  if (!url) return "unknown";
  const u = url.toLowerCase();
  if (u.includes("youtube.com") || u.includes("youtu.be") || u.includes("youtube-nocookie.com")) return "youtube";
  if (u.includes("vimeo.com") || u.includes("player.vimeo.com")) return "vimeo";
  if (u.includes("archive.org")) return "archive";
  if (u.includes("dailymotion.com") || u.includes("dai.ly")) return "dailymotion";
  if (u.includes("odysee.com") || u.includes("lbry.tv")) return "odysee";
  if (u.includes("pbs.org") || u.includes("player.pbs.org")) return "pbs";
  if (u.includes("bilibili.com") || u.includes("b23.tv")) return "bilibili";
  if (u.includes("rumble.com")) return "rumble";
  if (u.includes("bitchute.com")) return "bitchute";
  if (u.includes("arte.tv")) return "arte";
  if (u.includes("africanews.com")) return "africanews";
  if (u.includes("euronews.com")) return "euronews";
  if (u.includes("upload.wikimedia.org")) return "wikimedia"; // Direct video file
  if (u.includes("commons.wikimedia.org")) return "wikimedia";
  if (u.includes("c-span.org") || u.includes("c-spanvideo.org")) return "cspan";
  if (u.includes("images.nasa.gov") || u.includes("images-assets.nasa.gov") || u.includes("nasa.gov/video")) return "nasa";
  if (/fox\d+\w*\.com|foxtv\.com|static-clips\.fox\.com/.test(u)) return "fox";
  if (u.includes("cbsnews.com") || u.includes("cbsivideo.com")) return "cbsnews";
  if (u.includes("ted.com") || u.includes("embed.ted.com")) return "ted";
  if (u.includes("rtve.es")) return "rtve";
  if (u.includes("streamable.com")) return "streamable";
  if (u.includes("clips.twitch.tv") || u.includes("twitch.tv")) return "twitch";
  if (u.includes("pluto.tv")) return "pluto";
  if (u.includes("tiktok.com/") && (u.includes("/video/") || u.includes("/@"))) return "tiktok";
  if (u.includes("soundcloud.com/") && !u.includes("/player/")) return "soundcloud";
  if (u.includes("audius.co/") || u.includes("audius.org/") || u.includes("discoveryprovider.audius.co")) return "audius";
  if (u.includes("pixabay.com/videos/")) return "pixabay";
  if (u.includes("pexels.com/video/")) return "pexels";
  if (u.includes("coub.com/view/") || u.includes("coub.com/embed/")) return "coub";
  if (u.includes("hearthis.at/")) return "hearthis";
  if (u.includes("3speak.tv") || u.includes("3speak.online")) return "threespeak";
  if (u.includes("/videos/watch/") || u.includes("/videos/embed/") || u.includes("/w/")) return "peertube";
  return "unknown";
}

// ══════════════════════════════════════════════════
// TIER CLASSIFICATION
// ══════════════════════════════════════════════════
//
// "iframe"  = embed via <iframe> (YouTube, Vimeo, DM, PeerTube, Odysee, TED, RTVE, etc.)
// "video"   = direct <video> tag (Wikimedia, NASA, Fox, Streamable, BitChute, Euronews, etc.)
// "archive" = Archive.org special case (use their embed player iframe)

export function getPlayerTier(platform, url) {
  // Tier: direct <video> tag
  const directVideoSources = [
    "fox",
    "bitchute", "streamable", "cbsnews",
    "nasa", "euronews", "africanews", "wikimedia", "pixabay", "pexels",
  ];
  if (directVideoSources.includes(platform)) return "video";

  // If the URL itself is a direct media file, use <video>
  if (platform === "unknown" && url && /\.(mp4|webm|m3u8|ogg|ogv)(\?|#|$)/i.test(url)) return "video";

  // Archive.org: their /embed/ player handles everything
  if (platform === "archive") return "iframe";

  // Everything else: iframe embed
  return "iframe";
}

// ══════════════════════════════════════════════════
// EMBED URL EXTRACTION
// ══════════════════════════════════════════════════

/**
 * Given a video URL (and optional _embedUrl from universal search),
 * return the best embed/playback URL for the playlist maker preview.
 * 
 * If _embedUrl is provided by the backend, prefer it — it's already normalized.
 */
// Normalize Wikimedia URLs: strip /thumb/, convert .ogv to .webm transcode
export function normalizeWikimediaUrl(u) {
  if (!u) return u;
  let url = u;
  // Handle Special:FilePath redirect URLs — convert to API-based transcode
  const fpMatch = url.match(/commons\.wikimedia\.org\/wiki\/Special:FilePath\/(.+)/);
  if (fpMatch && /\.(ogv|mpg)(\?|#|$)/i.test(url)) {
    // Can't derive upload path without MD5, but we can use the Commons thumbnail URL
    // which is in the same data. For now, fall through — these need extension-level fix.
    // Return the URL as-is so the error handler shows "Watch on Wikimedia" link.
  }
  // Strip /thumb/ from URL path
  url = url.replace(/\/thumb\//, '/');
  // Remove trailing size spec like /400px-Filename.ext or /500px--Filename.ext
  url = url.replace(/\/\d+px--?[^/]*$/, '');
  // .ogv and .mpg can't play natively in modern browsers — use Wikimedia .360p.webm transcode
  if (/upload\.wikimedia\.org/.test(url) && /\.(ogv|mpg)(\?|#|$)/i.test(url) && url.includes('/commons/')) {
    var base = url.split('?')[0].split('#')[0];
    var filename = base.split('/').pop();
    url = base.replace('/commons/', '/commons/transcoded/') + '/' + filename + '.360p.webm';
  }
  return url;
}

export function getPreviewUrl(url, embedUrl) {
  // If the backend already gave us an embed URL, use it
  // But NOT if it's HTML (e.g. SoundCloud oEmbed returns <iframe> html)
  if (embedUrl && !embedUrl.trim().startsWith("<")) return embedUrl;

  const platform = getVideoPlatform(url);
  switch (platform) {
    case "youtube":     return extractYouTubeEmbed(url);
    case "vimeo":       return extractVimeoEmbed(url);
    case "archive":     return extractArchiveEmbed(url);
    case "dailymotion": return extractDailymotionEmbed(url);
    case "odysee":      return extractOdyseeEmbed(url);
    case "peertube":    return extractPeerTubeEmbed(url);
    case "pbs":         return extractPbsEmbed(url);
    case "bilibili":    return extractBilibiliEmbed(url);
    case "rumble":      return extractRumbleEmbed(url);
    case "bitchute":    return extractBitChuteEmbed(url);
    case "arte":        return extractArteEmbed(url);
    case "ted":         return extractTedEmbed(url);
    case "rtve":        return extractRtveEmbed(url);
    case "twitch":      return extractTwitchEmbed(url);
    case "cspan":       return url; // JWPlayer URLs pass through
    case "streamable":  return extractStreamableEmbed(url);
    case "wikimedia":   return extractWikimediaEmbed(url);
    case "nasa":        return url;  // already a direct MP4
    case "fox":         return url;
    case "euronews":
      if (/\.(mp4|webm|m3u8)(\?|#|$)/i.test(url)) return extractDirectVideoEmbed(url);
      return "";
    case "africanews":  return extractDirectVideoEmbed(url);
    case "cbsnews":     return url;
    case "pluto":       return url;
    case "tiktok":      return extractTikTokEmbed(url);
    case "soundcloud":  return extractSoundCloudEmbed(url, embedUrl || "");
    case "audius":      return extractAudiusEmbed(url, embedUrl);
    case "pixabay":     return extractDirectVideoEmbed(url);
    case "pexels":      return extractDirectVideoEmbed(url);
    case "coub":        return extractCoubEmbed(url);
    case "hearthis":    return extractHearthisEmbed(url, embedUrl);
    case "threespeak":  return extractThreeSpeakEmbed(url);
    default:            return url;
  }
}

// ── Individual extractors (ported from platformUtils.ts) ──

function extractYouTubeEmbed(url) {
  const patterns = [/(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/, /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/, /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/];
  for (const p of patterns) { const m = url.match(p); if (m) return `https://www.youtube.com/embed/${m[1]}?autoplay=1&mute=1&controls=1`; }
  return url;
}

function extractVimeoEmbed(url) {
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? `https://player.vimeo.com/video/${m[1]}?autoplay=1&controls=1` : url;
}

function extractArchiveEmbed(url) {
  if (url.includes("/embed/")) return url;
  const m = url.match(/archive\.org\/details\/(.+?)(?:\?|$)/);
  return m ? `https://archive.org/embed/${m[1]}?autoplay=1` : url;
}

function extractDailymotionEmbed(url) {
  const patterns = [/dailymotion\.com\/video\/([a-zA-Z0-9]+)/, /dai\.ly\/([a-zA-Z0-9]+)/];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return `https://geo.dailymotion.com/player.html?video=${m[1]}`;
  }
  return url;
}

function extractOdyseeEmbed(url) {
  if (url.includes("/$/embed/")) return url;
  const m = url.match(/odysee\.com\/(@[^?#]+)/);
  if (m) return `https://odysee.com/$/embed/${m[1]}`;
  const pathMatch = url.match(/odysee\.com\/([^?#]+)/);
  if (pathMatch) return `https://odysee.com/$/embed/${pathMatch[1]}`;
  return url;
}

function extractPeerTubeEmbed(url) {
  if (url.includes("/videos/embed/")) return url;
  const m = url.match(/(https?:\/\/[^/]+)\/(?:videos\/watch|w)\/([^/?#]+)/);
  return m ? `${m[1]}/videos/embed/${m[2]}?autoplay=1&warningTitle=0&p2p=0&peertubeLink=0` : url;
}

function extractPbsEmbed(url) {
  if (url.includes("viralplayer") || url.includes("partnerplayer")) return url;
  const m = url.match(/pbs\.org\/video\/([^/?#]+)/);
  if (m) return `https://player.pbs.org/viralplayer/${m[1]}/`;
  return url;
}

function extractBilibiliEmbed(url) {
  if (url.includes("player.bilibili.com")) return url;
  const m = url.match(/bilibili\.com\/video\/(BV[a-zA-Z0-9]+)/);
  if (m) return `https://player.bilibili.com/player.html?bvid=${m[1]}&danmaku=0`;
  return url;
}

function extractRumbleEmbed(url) {
  if (url.includes("/embed/")) return url;
  const m = url.match(/rumble\.com\/([a-zA-Z0-9-]+)\.html/);
  if (m) return `https://rumble.com/embed/${m[1]}/`;
  return url;
}

function extractBitChuteEmbed(url) {
  if (url.includes("/embed/")) return url;
  const m = url.match(/bitchute\.com\/video\/([^/?#]+)/);
  return m ? `https://www.bitchute.com/embed/${m[1]}/` : url;
}

function extractArteEmbed(url) {
  if (url.includes("/embeds/")) return url;
  const m = url.match(/arte\.tv\/en\/videos\/([^/]+)/);
  return m ? `https://www.arte.tv/embeds/en/${m[1]}` : url;
}

function extractTedEmbed(url) {
  if (url.includes("embed.ted.com")) return url;
  const m = url.match(/ted\.com\/talks\/([^/?#]+)/);
  return m ? `https://embed.ted.com/talks/${m[1]}` : url;
}

function extractRtveEmbed(url) {
  if (url.includes("/drmn/embed/")) return url;
  const m = url.match(/rtve\.es\/play\/videos\/(\d+)/);
  return m ? `https://www.rtve.es/drmn/embed/video/${m[1]}` : url;
}

function extractStreamableEmbed(url) {
  if (url.includes("/o/")) return url;
  const m = url.match(/streamable\.com\/([a-zA-Z0-9]+)/);
  return m ? `https://streamable.com/o/${m[1]}` : url;
}

function extractTwitchEmbed(url) {
  // clips.twitch.tv/embed?clip=SLUG or clips.twitch.tv/SLUG or twitch.tv/CHANNEL/clip/SLUG
  var slug = null;
  // Already an embed URL with ?clip= param
  var mq = url.match(/[?&]clip=([A-Za-z0-9_-]+)/);
  if (mq) slug = mq[1];
  if (!slug) {
    var m1 = url.match(/clips\.twitch\.tv\/([^/?#]+)/);
    if (m1 && m1[1] !== 'embed') slug = m1[1];
  }
  if (slug === null) {
    var m2 = url.match(/twitch\.tv\/[^/]+\/clip\/([^/?#]+)/);
    if (m2) slug = m2[1];
  }
  if (slug === null) return url;
  var host = typeof window !== "undefined" ? window.location.hostname : "tools.tvadi.com";
  var parentParams = "&parent=" + host;
  if (host !== "tvadi.com") parentParams += "&parent=tvadi.com";
  return "https://clips.twitch.tv/embed?clip=" + slug + parentParams + "&autoplay=true&muted=true";
}

// ── TikTok: iframe embed with postMessage control ──
function extractTikTokEmbed(url) {
  // Extract video ID from tiktok.com/@user/video/1234567890
  var m = url.match(/tiktok\.com\/@[^/]+\/video\/(\d+)/);
  if (m) return "https://www.tiktok.com/player/v1/" + m[1] + "?autoplay=1&mute=1&controls=1&rel=0&music_info=1";
  // Already a player URL
  if (url.includes("/player/")) return url;
  return url;
}

// ── SoundCloud: widget iframe with postMessage control ──
function extractSoundCloudEmbed(url, embedCode) {
  // If embedCode contains the widget URL (from oEmbed html), extract and use it
  if (embedCode) {
    // oEmbed returns iframe html with src="https://w.soundcloud.com/player/?url=..."
    var srcMatch = embedCode.match(/src=["']([^"']+)/);
    if (srcMatch && srcMatch[1].includes("w.soundcloud.com/player")) {
      var widgetUrl = srcMatch[1].replace(/&amp;/g, "&");
      // Ensure visual=true and auto_play=true
      if (!widgetUrl.includes("visual=")) widgetUrl += "&visual=true";
      if (!widgetUrl.includes("auto_play=")) widgetUrl += "&auto_play=true";
      return widgetUrl;
    }
    // embedCode itself might be a direct widget URL
    if (embedCode.includes("w.soundcloud.com/player")) {
      var ec = embedCode.replace(/&amp;/g, "&");
      if (!ec.includes("visual=")) ec += "&visual=true";
      if (!ec.includes("auto_play=")) ec += "&auto_play=true";
      return ec;
    }
  }
  // Don't double-wrap if already a widget URL
  if (url.includes("w.soundcloud.com/player")) return url;
  // Page URL — wrap in widget (needs api.soundcloud.com URL, so this is a fallback)
  return "https://w.soundcloud.com/player/?url=" + encodeURIComponent(url) + "&visual=true&auto_play=true";
}

// ── Audius: direct MP3 stream via <audio> or embed iframe ──
function extractAudiusEmbed(url, embedCode) {
  // If embedCode has the stream URL, use it directly (player renders <audio>)
  if (embedCode && embedCode.includes("discoveryprovider.audius.co")) {
    return embedCode;
  }
  // If embedCode has another stream URL format
  if (embedCode && embedCode.includes("/stream")) {
    return embedCode;
  }
  // Fallback: Audius embed player iframe
  var m = url.match(/audius\.co\/([^/]+)\/([^/?#]+)/);
  if (m) return "https://audius.co/embed/track?handle=" + m[1] + "&title=" + m[2] + "&flavor=card";
  return url;
}

// ══════════════════════════════════════════════════
// DISPLAY HELPERS
// ══════════════════════════════════════════════════

function extractWikimediaEmbed(url) {
  if (!url) return url;
  // Check if this is a Special:FilePath URL with non-web-playable format
  var fpMatch = url.match(/Special:FilePath\/(.+)/);
  if (fpMatch && /\.(ogv|mpg|mpeg)(\?|#|$)/i.test(url)) {
    // Resolve the redirect inside the iframe to get the upload URL, then transcode
    return "data:text/html;charset=utf-8," + encodeURIComponent(
      '<!DOCTYPE html><html><body style="margin:0;background:#000;display:flex;align-items:center;justify-content:center;height:100vh">' +
      '<video id="v" style="width:100%;height:100%;object-fit:contain" controls autoplay muted></video>' +
      '<script>' +
      'fetch("' + url + '",{method:"HEAD"}).then(function(r){' +
      '  var u=r.url;' +
      '  u=u.replace("/commons/","/commons/transcoded/");' +
      '  var fn=decodeURIComponent(u.split("/").pop());' +
      '  u=u+"/"+encodeURIComponent(fn)+".360p.webm";' +
      '  document.getElementById("v").src=u;' +
      '}).catch(function(){document.getElementById("v").src="' + url + '";});' +
      '<\/script></body></html>'
    );
  }
  // Normalize (strip /thumb/, size prefix, .ogv/.mpg -> transcode)
  var normalized = normalizeWikimediaUrl(url);
  return "data:text/html;charset=utf-8," + encodeURIComponent(
    '<!DOCTYPE html><html><body style="margin:0;background:#000;display:flex;align-items:center;justify-content:center;height:100vh">' +
    '<video src="' + normalized + '" style="width:100%;height:100%;object-fit:contain" controls autoplay muted></video>' +
    '</body></html>'
  );
}

// Wrap direct video URL in data: iframe to avoid OpaqueResponseBlocking
function extractDirectVideoEmbed(url) {
  if (!url) return url;
  if (url.startsWith("data:")) return url;
  // Only wrap actual video file URLs — not page URLs
  if (/\.(mp4|webm|ogv|ogg|m3u8)(\?|#|$)/i.test(url) || url.includes("images-assets.nasa.gov")) {
    return "data:text/html;charset=utf-8," + encodeURIComponent(
      '<!DOCTYPE html><html><body style="margin:0;background:#000;display:flex;align-items:center;justify-content:center;height:100vh">' +
      '<video src="' + url + '" style="width:100%;height:100%;object-fit:contain" controls autoplay muted></video>' +
      '</body></html>'
    );
  }
  return url;
}

// ── Coub: short-form looping video ──
function extractCoubEmbed(url) {
  var m = url.match(/coub\.com\/(?:view|embed)\/([a-zA-Z0-9]+)/);
  if (m) return "https://coub.com/embed/" + m[1] + "?autostart=true&muted=true&startWithHD=true";
  return url;
}

// ── hearthis.at: DJ mixes/electronic music ──
function extractHearthisEmbed(url, embedCode) {
  if (embedCode) {
    var numMatch = embedCode.match(/hearthis\.at\/embed\/(\d+)/);
    if (numMatch) return "https://hearthis.at/embed/" + numMatch[1] + "/transparent/?style=2&block_size=2&block_space=2&background=1&autoplay=1&hcolor=CC0FE0";
    if (embedCode.includes("hearthis.at/embed/")) {
      var u = embedCode.trim();
      if (!u.includes("autoplay=")) u += (u.includes("?") ? "&" : "?") + "autoplay=1";
      return u;
    }
  }
  var m = url.match(/hearthis\.at\/([^/]+)\/([^/?#]+)/);
  if (m && m[1] !== "embed") return "https://hearthis.at/embed/" + m[1] + "/" + m[2] + "/transparent/?style=2&block_size=2&block_space=2&background=1&autoplay=1&hcolor=CC0FE0";
  return url;
}

// ── 3Speak: Hive blockchain video ──
function extractThreeSpeakEmbed(url) {
  var m = url.match(/3speak\.(?:tv|online)\/watch\?v=([^/]+)\/([^&]+)/);
  if (m) return "https://play.3speak.tv/embed?v=" + m[1] + "/" + m[2] + "&autoplay=1&muted=1";
  if (url.includes("play.3speak.tv/embed")) {
    if (!url.includes("autoplay=")) url += (url.includes("?") ? "&" : "?") + "autoplay=1&muted=1";
    return url;
  }
  return url;
}

export const PLATFORM_COLORS = {
  youtube: "#FF0000", vimeo: "#1AB7EA", archive: "#428BCA",
  dailymotion: "#00D2F3", odysee: "#EF370E", pbs: "#2638C4",
  bilibili: "#00A1D6", rumble: "#85C742", bitchute: "#EF4E3F",
  peertube: "#F2690D", arte: "#FA5A28", euronews: "#003366",
  africanews: "#0078A8", wikimedia: "#006699", cspan: "#2C2E6E",
  nasa: "#0B3D91", fox: "#003366", cbsnews: "#1A6AB5",
  ted: "#E62B1E", rtve: "#0062A0", streamable: "#0091FF",
  pluto: "#1A1A2E", twitch: "#9146FF", tiktok: "#FF2D55", soundcloud: "#FF5500", audius: "#CC0FE0", pixabay: "#2EC66D", pexels: "#05A081", coub: "#FF6600", hearthis: "#CC0FE0", threespeak: "#E21E3C", unknown: "#666666",
};

export const PLATFORM_NAMES = {
  youtube: "YouTube", vimeo: "Vimeo", archive: "Archive.org",
  dailymotion: "Dailymotion", odysee: "Odysee", pbs: "PBS",
  bilibili: "Bilibili", rumble: "Rumble", bitchute: "BitChute",
  peertube: "PeerTube", arte: "Arte", euronews: "Euronews",
  africanews: "Africanews", wikimedia: "Wikimedia", cspan: "C-SPAN",
  nasa: "NASA", fox: "Fox", cbsnews: "CBS News",
  ted: "TED", rtve: "RTVE", streamable: "Streamable",
  pluto: "Pluto TV", twitch: "Twitch", tiktok: "TikTok", soundcloud: "SoundCloud", audius: "Audius (audio)", pixabay: "Pixabay", pexels: "Pexels", coub: "Coub", hearthis: "hearthis.at (audio)", threespeak: "3Speak", unknown: "Video",
};

export function getPlatformName(platform) {
  return PLATFORM_NAMES[platform] || "Video";
}

export function getPlatformColor(platform) {
  return PLATFORM_COLORS[platform] || "#666666";
}


// ══════════════════════════════════════════════════
// COMPAT LAYER — aliases used by TvadiVideoPlayer.jsx
// ══════════════════════════════════════════════════

export function extractEmbedUrl(url, embedCode, parentDomain) {
  return getPreviewUrl(url, embedCode);
}

export function platformUsesDirectVideo(platform, url, _unused, embedCode) {
  return getPlayerTier(platform, url) === "video";
}

export function getPlatformDisplayName(platform) {
  return getPlatformName(platform);
}
